import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  FALHUB_APP_STORE_URL,
  FALHUB_GOOGLE_PLAY_URL,
} from "../../helpers/webConstants";

const AppleLogoIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M16.365 1.43c0 1.14-.417 2.087-1.25 2.84-.9.82-1.98 1.23-3.24 1.22-.05-1.1.4-2.14 1.26-2.95.86-.83 1.96-1.28 3.23-1.11.01.14.01.28 0 .42zm1.24 3.56c-1.77-.1-3.28.99-4.13.99-.87 0-2.21-.96-3.64-.93-1.87.03-3.59 1.09-4.55 2.77-1.95 3.38-.5 8.39 1.4 11.14.93 1.35 2.03 2.86 3.48 2.81 1.4-.05 1.93-.9 3.62-.9 1.69 0 2.15.9 3.62.87 1.5-.03 2.45-1.37 3.37-2.73 1.06-1.55 1.5-3.05 1.52-3.13-.03-.01-2.92-1.12-2.95-4.45-.03-2.78 2.28-4.11 2.38-4.17-1.3-1.9-3.32-2.14-4.02-2.18z"
    />
  </svg>
);

/** Official Google Play prism (4 segments, Google brand colors). */
const GooglePlayLogoIcon = ({ size = 28 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      fill="#FBBC04"
      d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594z"
    />
    <path
      fill="#4285F4"
      d="M1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924z"
    />
    <path
      fill="#34A853"
      d="M13.544 11.989l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973z"
    />
    <path
      fill="#EA4335"
      d="M14.49 13.235l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z"
    />
  </svg>
);

/**
 * App Store + Google Play download badges (CSS/SVG only, no image assets).
 */
const AppStoreDownloadBadges = ({ variant = "about" }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isArabic = language === "ar";

  if (variant === "footer") {
    return (
      <FooterDownloadBlock $isArabic={isArabic}>
        <FooterTitle>{t("download_the_app")}</FooterTitle>
        <IconBadgesRow>
          <IconBadgeLink
            href={FALHUB_APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("download_app_store")}
          >
            <AppleLogoIcon />
          </IconBadgeLink>
          <IconBadgeLink
            href={FALHUB_GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("download_google_play")}
          >
            <GooglePlayLogoIcon size={26} />
          </IconBadgeLink>
        </IconBadgesRow>
      </FooterDownloadBlock>
    );
  }

  return (
    <BadgesRow $isArabic={isArabic}>
      <StoreBadgeLink
        href={FALHUB_APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("download_app_store")}
        $isArabic={isArabic}
      >
        <BadgeIconWrap>
          <AppleLogoIcon />
        </BadgeIconWrap>
        <BadgeText $isArabic={isArabic}>
          <BadgeLineSmall>{t("download_on_the")}</BadgeLineSmall>
          <BadgeLineLarge>{t("app_store")}</BadgeLineLarge>
        </BadgeText>
      </StoreBadgeLink>

      <StoreBadgeLink
        href={FALHUB_GOOGLE_PLAY_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("download_google_play")}
        $isArabic={isArabic}
      >
        <BadgeIconWrap>
          <GooglePlayLogoIcon />
        </BadgeIconWrap>
        <BadgeText $isArabic={isArabic}>
          <BadgeLineSmall>{t("get_it_on")}</BadgeLineSmall>
          <BadgeLineLarge>{t("google_play")}</BadgeLineLarge>
        </BadgeText>
      </StoreBadgeLink>
    </BadgesRow>
  );
};

export default AppStoreDownloadBadges;

const BadgesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 24px;
  direction: ${(props) => (props.$isArabic ? "rtl" : "ltr")};
  justify-content: ${(props) => (props.$isArabic ? "flex-end" : "flex-start")};

  @media (max-width: 575px) {
    flex-direction: column;
    align-items: ${(props) => (props.$isArabic ? "flex-end" : "flex-start")};
  }
`;

const StoreBadgeLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 168px;
  padding: 10px 16px;
  background: #000000;
  color: #ffffff;
  border-radius: 12px;
  text-decoration: none;
  transition: opacity 0.2s ease;
  flex-direction: ${(props) => (props.$isArabic ? "row-reverse" : "row")};

  &:hover {
    opacity: 0.85;
    color: #ffffff;
    text-decoration: none;
  }
`;

const BadgeIconWrap = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #ffffff;
`;

const BadgeText = styled.span`
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  text-align: ${(props) => (props.$isArabic ? "right" : "left")};
`;

const BadgeLineSmall = styled.span`
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.02em;
`;

const BadgeLineLarge = styled.span`
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const FooterDownloadBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$isArabic ? "flex-end" : "flex-start")};
  gap: 12px;
  direction: ${(props) => (props.$isArabic ? "rtl" : "ltr")};

  @media (max-width: 1199px) {
    align-items: center;
  }
`;

const FooterTitle = styled.h3`
  margin: 0;
  color: #ffffff;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.2;
`;

const IconBadgesRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const IconBadgeLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border: 1.5px solid #ffffff;
  border-radius: 12px;
  background: transparent;
  color: #ffffff;
  text-decoration: none;
  transition: opacity 0.2s ease, background 0.2s ease;

  &:hover {
    opacity: 0.9;
    background: rgba(255, 255, 255, 0.08);
    text-decoration: none;
  }
`;
